import {
  CheckoutItem,
  EntityType,
} from "../../api/grpc/subscription/subscription";
import { formatPrice } from "./productStorePrices";
import { PurchaseAddOn, PurchaseOffer } from "./productStorePurchase";

export const MIN_QUANTITY = 1;
export const MAX_QUANTITY = 99;

export interface CartState {
  /** The main product's quantity. */
  quantity: number;
  /** Selected add-on ID → its own quantity (used by `independent` add-ons). */
  addOns: Record<string, number>;
}

export type CartAction =
  | { type: "setQuantity"; quantity: number }
  | { type: "setAddOnQuantity"; id: string; quantity: number }
  | { type: "toggleAddOn"; addOn: PurchaseAddOn; addOns: PurchaseAddOn[] };

export interface CartSummaryLine {
  id: string;
  title: string;
  quantity: number;
  total: string;
}

export interface CartSubscriptionLine {
  id: string;
  title: string;
  /** Per term, e.g. "$750 per year"; today's amount is prorated. */
  price: string;
  entityType?: EntityType;
}

export interface CartSummary {
  /** Paid once: products, one-time add-ons and shipping. */
  today: CartSummaryLine[];
  subtotal: string;
  /** Added to the subscription and renewed with it. */
  subscription: CartSubscriptionLine[];
  items: CheckoutItem[];
}

export const createCart = (): CartState => ({
  quantity: MIN_QUANTITY,
  addOns: {},
});

const clampQuantity = (quantity: number) =>
  Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, Math.round(quantity)));

const without = (addOns: Record<string, number>, id: string) => {
  const next = { ...addOns };
  delete next[id];
  return next;
};

/** Selected add-ons that need this one, e.g. PREMIUM CARE locks CARE. */
export const getRequiredBy = (
  id: string,
  cart: CartState,
  addOns: PurchaseAddOn[]
) => addOns.filter((addOn) => addOn.requires === id && addOn.id in cart.addOns);

export const cartReducer = (
  state: CartState,
  action: CartAction
): CartState => {
  switch (action.type) {
    case "setQuantity":
      return { ...state, quantity: clampQuantity(action.quantity) };

    case "setAddOnQuantity":
      return action.id in state.addOns
        ? {
            ...state,
            addOns: {
              ...state.addOns,
              [action.id]: clampQuantity(action.quantity),
            },
          }
        : state;

    case "toggleAddOn": {
      const { id, requires } = action.addOn;

      if (id in state.addOns) {
        // A required add-on stays until the one needing it is cleared.
        return getRequiredBy(id, state, action.addOns).length
          ? state
          : { ...state, addOns: without(state.addOns, id) };
      }

      return {
        ...state,
        addOns: {
          ...state.addOns,
          [id]: MIN_QUANTITY,
          ...(requires
            ? { [requires]: state.addOns[requires] ?? MIN_QUANTITY }
            : {}),
        },
      };
    }
  }
};

/** How many of an add-on are ordered, following its quantity rule. */
export const getAddOnQuantity = (addOn: PurchaseAddOn, cart: CartState) => {
  if (addOn.quantity === "matchMain") {
    return cart.quantity;
  }

  return addOn.quantity === "fixed" ? 1 : (cart.addOns[addOn.id] ?? 1);
};

export const getCartSummary = (
  offer: PurchaseOffer,
  cart: CartState
): CartSummary => {
  const { product, currency, shipping } = offer;
  const lines = [
    { item: product, quantity: cart.quantity },
    ...offer.addOns
      .filter(({ id }) => id in cart.addOns)
      .map((addOn) => ({
        item: addOn,
        quantity: getAddOnQuantity(addOn, cart),
      })),
  ];
  const oneTime = lines.filter(({ item }) => item.kind === "one-time");
  const today: CartSummaryLine[] = oneTime.map(({ item, quantity }) => ({
    id: item.id,
    title: item.title,
    quantity,
    total: formatPrice(item.unitCents * quantity, currency),
  }));
  // Summed in minor units, so totals have no rounding errors.
  let subtotalCents = oneTime.reduce(
    (sum, { item, quantity }) => sum + item.unitCents * quantity,
    0
  );

  if (shipping) {
    today.push({
      id: "shipping",
      title: "Shipping",
      quantity: 1,
      total: formatPrice(shipping.priceCents, currency),
    });
    subtotalCents += shipping.priceCents;
  }

  return {
    today,
    subtotal: formatPrice(subtotalCents, currency),
    subscription: lines
      .filter(({ item }) => item.kind === "subscription")
      .map(({ item }) => ({
        id: item.id,
        title: item.title,
        price: `${item.unitPrice} ${item.unitLabel}`,
        entityType: item.entityType,
      })),
    items: [
      ...lines.map(({ item, quantity }) => ({
        itemPriceID: item.priceId,
        quantity,
      })),
      ...(shipping ? [{ itemPriceID: shipping.priceId, quantity: 1 }] : []),
    ],
  };
};

/**
 * Which backend call checks the cart out. `CreateCartCheckout` needs at least
 * one add-on, and `CreateOneTimeCheckout` can't take add-ons.
 */
export const getCheckoutRoute = ({
  today,
  subscription,
}: CartSummary): "oneTime" | "subscription" | "cart" => {
  if (!subscription.length) {
    return "oneTime";
  }

  return today.length ? "cart" : "subscription";
};
